import type { ResponsePrepTime, VideoPlayerConfig } from "@memorang/types";
import { type ReactNode, useRef } from "react";
import AudioPlayerContainer, {
	type AudioPlayerRef,
} from "../../../../AudioPlayerContainer";
import Box from "../../../../Box";
import HintAccordion, {
	type HintAccordionProps,
} from "../../../../HintAccordion";
import {
	SessionVideoPlayer,
	type SessionVideoPlayerRef,
} from "../../../../SessionVideoPlayer";
import usePrepSequence from "../../../hooks/usePrepSequence";
import ChoicesList, { type ChoicesListProps } from "../../ChoicesList";
import ItemStem from "../../ItemStem";
import { ItemInstruction } from "../pte/ItemInstruction";
import { PrepCountdownBanner } from "../pte/PrepCountdownBanner";
import { SubInstruction } from "../pte/SubInstruction";

type Props = Pick<
	ChoicesListProps,
	| "choices"
	| "itemId"
	| "handleSelectChoice"
	| "selectedChoiceIds"
	| "correctAnswerIds"
	| "crossedOutChoiceIds"
	| "isAnswered"
	| "useCardWrapper"
	| "showCrossOut"
	| "numChoicesRequired"
	| "isSummativeTest"
> & {
	audioUri: string;
	videoUri?: string;
	instruction: string;
	subInstruction?: string;
	stem?: string;
	multiSelect?: boolean;
	shuffle?: boolean;
	hintAccordionProps?: HintAccordionProps;
	renderHint?: () => ReactNode;
	prepTime?: ResponsePrepTime;
	allowAudioReplay?: boolean;
	allowVideoReplay?: boolean;
	videoPlayerConfig?: VideoPlayerConfig;
	progressSeekable?: boolean;
	onAudioEnded?: () => void;
	renderFeedback?: () => ReactNode;
};

const AudioChoiceItem = ({
	audioUri,
	videoUri,
	instruction,
	subInstruction,
	stem,
	choices,
	itemId,
	handleSelectChoice,
	selectedChoiceIds,
	correctAnswerIds,
	crossedOutChoiceIds,
	isAnswered,
	useCardWrapper,
	showCrossOut,
	numChoicesRequired,
	isSummativeTest,
	multiSelect = false,
	shuffle = false,
	hintAccordionProps,
	renderHint,
	prepTime,
	allowAudioReplay,
	allowVideoReplay,
	videoPlayerConfig,
	progressSeekable,
	onAudioEnded,
	renderFeedback,
}: Props) => {
	const audioPlayerRef = useRef<AudioPlayerRef | null>(null);
	const videoPlayerRef = useRef<SessionVideoPlayerRef | null>(null);
	const isTimed = prepTime != null;
	const playOnce = !allowAudioReplay;
	const videoPlayOnce = !allowVideoReplay;

	const { remainingPrepSeconds, showBanner } = usePrepSequence({
		prepTime,
		hasAudioStimulus: Boolean(audioUri),
		hasVideoStimulus: Boolean(videoUri),
		hasRecorder: false,
		audioPlayerRef,
		videoPlayerRef,
		onAudioEnded,
		enabled: isTimed && !isAnswered,
		waitForPlayer: true,
	});

	return (
		<Box gap={16}>
			<Box gap={8}>
				<ItemInstruction instruction={instruction} />
				{subInstruction ? <SubInstruction text={subInstruction} /> : null}
				{showBanner ? (
					<PrepCountdownBanner
						remainingSeconds={remainingPrepSeconds}
						playOnce={videoUri ? videoPlayOnce : playOnce}
						isVideo={Boolean(videoUri)}
					/>
				) : null}
				{videoUri ? (
					<SessionVideoPlayer
						ref={videoPlayerRef}
						uri={videoUri}
						playOnce={videoPlayOnce}
						videoPlayerConfig={videoPlayerConfig}
						progressSeekable={progressSeekable}
					/>
				) : null}
				{audioUri ? (
					<AudioPlayerContainer
						ref={audioPlayerRef}
						uri={audioUri}
						playOnce={playOnce}
					/>
				) : null}
			</Box>
			{stem ? <ItemStem text={stem} /> : null}
			<ChoicesList
				choices={choices}
				handleSelectChoice={handleSelectChoice}
				selectedChoiceIds={selectedChoiceIds}
				correctAnswerIds={correctAnswerIds}
				crossedOutChoiceIds={crossedOutChoiceIds}
				isAnswered={isAnswered}
				useCardWrapper={useCardWrapper}
				showCrossOut={showCrossOut}
				multiSelect={multiSelect}
				isSummativeTest={isSummativeTest}
				itemId={itemId}
				numChoicesRequired={numChoicesRequired}
				shuffle={shuffle}
			/>
			{renderHint?.() ??
				(hintAccordionProps ? <HintAccordion {...hintAccordionProps} /> : null)}
			{renderFeedback?.()}
		</Box>
	);
};

export default AudioChoiceItem;
